/**
 * DocRow — one document row in the HubList.
 *
 * Compact horizontal layout (launch-film screen 2 "SOURCES" list):
 *   [PDF/DOC/XLS icon] filename ............ [✓ indexed | status pill]
 *                      ConfPill · doctype-badge · [blocking?] · signals
 *
 * The DocumentRow data model (from M2) carries everything the row
 * needs — strategy doc_type + Spec 32 source_doctype + per-tag
 * confidences + verified flag + blocking flag + low flag.
 *
 * Click → fires onClick(doc); HubDetail (M6) is the eventual
 * destination. The row itself is purely presentational; the
 * parent owns selection state.
 */

import { Button, makeStyles, mergeClasses } from "@fluentui/react-components";
import { ArrowDownload24Regular, Delete24Regular } from "@fluentui/react-icons";
import type React from "react";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../../tokens";
import { isDerivedFile } from "../mergeUploadsAndInventory";
import type { DocumentRow } from "../types";
import { ConfidenceBar } from "./ConfidenceBar";
import { EvidenceSignals } from "./EvidenceSignals";

export interface DocRowProps {
  row: DocumentRow;
  onClick?: (row: DocumentRow) => void;
  onDeleteDoc?: (row: DocumentRow) => void;
  onDownloadDoc?: (row: DocumentRow) => void;
}

// DECISION: a deeper green than statusColors.cool for the "✓ indexed"
// text — cool (#26DF9B) is a fill colour and fails contrast as 10px text.
const INDEXED_GREEN = "#0E9F6E";

const useStyles = makeStyles({
  rowButton: {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: 0,
    backgroundColor: "transparent",
    border: "none",
    borderBottom: `1px solid ${surfaceColors.line}`,
    cursor: "pointer",
    fontFamily: fonts.body,
    ":hover": {
      backgroundColor: surfaceColors.paperDim,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "-2px",
    },
  },
  inner: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "10px",
    padding: "10px 14px",
  },
  // Small file-type icon (film: red "PDF" page). Colour = file kind.
  fileBadge: {
    width: "20px",
    height: "24px",
    borderRadius: "3px",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    paddingBottom: "3px",
    boxSizing: "border-box",
    fontSize: "6.5px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    letterSpacing: "0.2px",
    flexShrink: 0,
  },
  fileBadgePdf: {
    backgroundColor: "#FDECEF",
    color: statusColors.danger,
  },
  fileBadgeWord: {
    backgroundColor: "#E8EFFA",
    color: "#2B579A",
  },
  fileBadgeSheet: {
    backgroundColor: "#E6F4EC",
    color: "#1D6F42",
  },
  fileBadgeOther: {
    backgroundColor: surfaceColors.paperDim,
    color: inkColors.ink4,
  },
  // Right-aligned lifecycle status (film: green mono "✓ indexed").
  status: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "3px",
    flexShrink: 0,
    padding: "1px 6px",
    borderRadius: "9999px",
    fontSize: "10px",
    fontWeight: 600,
    fontFamily: fonts.mono,
    letterSpacing: "0.3px",
    whiteSpace: "nowrap",
    lineHeight: 1.5,
  },
  statusIndexed: {
    // Plain green text — no fill, like the film.
    color: INDEXED_GREEN,
    paddingLeft: 0,
    paddingRight: 0,
  },
  statusWorking: {
    backgroundColor: brandColors.light,
    color: brandColors.primary,
  },
  statusLimited: {
    backgroundColor: "#FEF6E3",
    color: "#7C2D12",
  },
  statusFailed: {
    backgroundColor: "#FCE7EC",
    color: statusColors.danger,
  },
  rowMain: {
    flex: 1,
    minWidth: 0,
  },
  filename: {
    fontSize: "12.5px",
    fontWeight: 500,
    color: inkColors.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginBottom: "3px",
  },
  metaRow: {
    ":empty": {
      display: "none",
    },
    marginTop: "2px",
    display: "flex",
    alignItems: "center",
    columnGap: "5px",
    rowGap: "3px",
    flexWrap: "wrap",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "3px",
    padding: "1px 5px",
    borderRadius: "3px",
    fontSize: "8.5px",
    fontWeight: 700,
    fontFamily: fonts.mono,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    border: "1px solid transparent",
  },
  pillVerified: {
    backgroundColor: "#E6FBF3",
    border: `1px solid ${statusColors.cool}`,
    color: "#0F5132",
  },
  pillHigh: {
    backgroundColor: "#E6FBF3",
    border: `1px solid ${statusColors.cool}`,
    color: "#0F5132",
  },
  pillMid: {
    backgroundColor: "#FEF6E3",
    border: `1px solid ${statusColors.warn}`,
    color: "#7C2D12",
  },
  pillLow: {
    backgroundColor: "#FCE7EC",
    border: `1px solid ${statusColors.danger}`,
    color: statusColors.danger,
  },
  doctypeBadge: {
    fontSize: "9.5px",
    fontWeight: 600,
    padding: "1px 6px",
    borderRadius: "3px",
    backgroundColor: brandColors.light,
    color: brandColors.primary,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  blockingBadge: {
    fontSize: "9.5px",
    fontWeight: 700,
    padding: "1px 6px",
    borderRadius: "3px",
    backgroundColor: "#FCE7EC",
    color: statusColors.danger,
    border: `1px solid ${statusColors.danger}`,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  sizeText: {
    fontSize: "9px",
    fontFamily: fonts.mono,
    color: inkColors.ink5,
  },
  paramRow: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "6px",
    rowGap: "3px",
    marginTop: "4px",
  },
  paramChip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "3px",
    padding: "1px 5px",
    borderRadius: "3px",
    fontSize: "8.5px",
    fontFamily: fonts.mono,
    backgroundColor: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.lineStrong}`,
    color: inkColors.ink3,
  },
  paramChipKey: {
    color: inkColors.ink4,
  },
  paramChipValue: {
    color: inkColors.ink,
    fontWeight: 600,
  },
  chevron: {
    marginTop: "4px",
    flexShrink: 0,
    color: inkColors.ink4,
  },
  deleteButton: {
    marginTop: "4px",
    marginLeft: "6px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    columnGap: "3px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 0,
    color: statusColors.danger,
    fontSize: "10px",
    fontWeight: 600,
    fontFamily: fonts.body,
    ":hover": {
      color: brandColors.primary,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },
  downloadButton: {
    marginTop: "4px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    columnGap: "3px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 0,
    color: inkColors.ink4,
    fontSize: "10px",
    fontWeight: 600,
    fontFamily: fonts.body,
    ":hover": {
      color: brandColors.primary,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },
  // ── Phase 2: AI intelligence additions ──────────────────────────
  filenameRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    minHeight: "24px",
  },
  confidenceBarWrap: {
    marginTop: "4px",
  },
});

type FileKind = "pdf" | "word" | "sheet" | "other";

function fileKind(filename: string): FileKind {
  const ext = filename.slice(filename.lastIndexOf(".") + 1).toLowerCase();
  if (ext === "pdf") return "pdf";
  if (ext === "doc" || ext === "docx" || ext === "rtf") return "word";
  if (ext === "xls" || ext === "xlsx" || ext === "csv") return "sheet";
  return "other";
}

function fileExtensionShort(filename: string): string {
  const idx = filename.lastIndexOf(".");
  if (idx === -1 || idx === filename.length - 1) return "FILE";
  return filename.slice(idx + 1, idx + 4).toUpperCase();
}

/**
 * Right-aligned lifecycle status. Maps ``processing_status`` (derived
 * from the upstream "Done" / "Pending" / "Parsing" / "Failed:*" string by
 * ``deriveProcessingStatus``): ready → "✓ indexed"; in-flight states →
 * violet working pill; limited → amber "⚠ no text"; failed → red.
 */
function RowStatus(props: { row: DocumentRow }): React.ReactElement {
  const styles = useStyles();
  const { row } = props;
  switch (row.processing_status) {
    case "ready":
      return (
        <span
          className={mergeClasses(styles.status, styles.statusIndexed)}
          aria-label="indexed"
          data-testid="docrow-status"
        >
          ✓ indexed
        </span>
      );
    case "uploading": {
      const pct = row.upload_progress_percent;
      return (
        <span className={mergeClasses(styles.status, styles.statusWorking)} data-testid="docrow-status">
          {typeof pct === "number" ? `uploading ${Math.round(pct)}%` : "uploading…"}
        </span>
      );
    }
    case "indexing":
      return (
        <span
          className={mergeClasses(styles.status, styles.statusWorking)}
          title={row.upstream_status ?? undefined}
          data-testid="docrow-status"
        >
          indexing…
        </span>
      );
    case "classifying":
      return (
        <span className={mergeClasses(styles.status, styles.statusWorking)} data-testid="docrow-status">
          classifying…
        </span>
      );
    case "limited":
      return (
        <span
          className={mergeClasses(styles.status, styles.statusLimited)}
          aria-label="text not indexed"
          title="Processed, but the text was not indexed (structure only)"
          data-testid="docrow-status"
        >
          ⚠ no text
        </span>
      );
    case "failed_upload":
    case "failed_processing":
      return (
        <span
          className={mergeClasses(styles.status, styles.statusFailed)}
          aria-label="failed"
          title={row.upload_error ?? row.upstream_status ?? undefined}
          data-testid="docrow-status"
        >
          ✕ failed
        </span>
      );
  }
}

function ConfPill(props: { row: DocumentRow }): React.ReactElement | null {
  const styles = useStyles();
  const { row } = props;

  // Verified always wins — once a writer has signed off, the
  // confidence pill flips to a unified "OK" badge regardless of
  // the underlying classifier confidence.
  if (row.verified) {
    return (
      <span className={mergeClasses(styles.pill, styles.pillVerified)} aria-label="verified">
        ✓ ok
      </span>
    );
  }

  // Limited / failed rows: the right-aligned RowStatus carries the
  // state; a confidence number would be meaningless next to it.
  if (
    row.processing_status === "limited" ||
    row.processing_status === "failed_upload" ||
    row.processing_status === "failed_processing"
  ) {
    return null;
  }

  // Source-doctype confidence preferred over strategy confidence
  // when both are present (the M6 detail view will surface BOTH).
  const sourceConf = row.source_classification?.source_doctype_confidence;
  if (typeof sourceConf === "number") {
    const pct = Math.round(sourceConf * 100);
    let cls = styles.pillLow;
    if (sourceConf >= 0.85) cls = styles.pillHigh;
    else if (sourceConf >= 0.6) cls = styles.pillMid;
    return (
      <span className={mergeClasses(styles.pill, cls)} aria-label={`confidence ${pct} percent`}>
        {pct}%
      </span>
    );
  }

  // Fallback to strategy confidence when no source-doctyping signal.
  const fallback = row.classification_confidence;
  if (fallback === "high") {
    return <span className={mergeClasses(styles.pill, styles.pillHigh)}>high</span>;
  }
  if (fallback === "medium") {
    return <span className={mergeClasses(styles.pill, styles.pillMid)}>med</span>;
  }
  if (fallback === "low") {
    return <span className={mergeClasses(styles.pill, styles.pillLow)}>low</span>;
  }
  return <span className={mergeClasses(styles.pill, styles.pillMid)}>?</span>;
}

export const DocRow: React.FC<DocRowProps> = ({ row, onClick, onDeleteDoc, onDownloadDoc }) => {
  const styles = useStyles();
  const handleClick = (): void => onClick?.(row);
  const handleDelete = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onDeleteDoc?.(row);
  };
  const handleDownload = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onDownloadDoc?.(row);
  };
  // ``doc_id`` is null only while an upload's POST is still in flight — there's
  // no stored file to pull yet, so hide the download affordance until it lands.
  const canDownload = Boolean(onDownloadDoc && row.doc_id);
  const showBlocking = row.blocking && !row.verified;
  const kind = fileKind(row.filename);
  return (
    // A plain <div role="button"> rather than a real <button> — the
    // delete affordance below is itself a <button>, and nesting
    // interactive controls (<button> inside <button>) is invalid HTML
    // that some WebViews (this ships inside an Office Add-in task
    // pane) dispatch inconsistently, occasionally double-firing the
    // inner control's click.
    <button
      type="button"
      tabIndex={0}
      className={styles.rowButton}
      onClick={handleClick}
      data-testid={`doc-row-${row.doc_id ?? row.filename}`}
      aria-label={`Document ${row.filename}`}
    >
      <div className={styles.inner}>
        <div
          className={mergeClasses(
            styles.fileBadge,
            kind === "pdf" && styles.fileBadgePdf,
            kind === "word" && styles.fileBadgeWord,
            kind === "sheet" && styles.fileBadgeSheet,
            kind === "other" && styles.fileBadgeOther,
          )}
          aria-hidden="true"
          data-testid="docrow-type"
        >
          {fileExtensionShort(row.filename)}
        </div>
        <div className={styles.rowMain}>
          <div className={styles.filenameRow}>
            <span className={styles.filename} style={{ marginBottom: 0, flex: 1, minWidth: 0 }}>
              {row.filename}
            </span>
            <RowStatus row={row} />
          </div>
          <div className={styles.metaRow}>
            <ConfPill row={row} />
            {isDerivedFile(row.filename) && (
              <span
                className={styles.doctypeBadge}
                title="Built by create_artefact from source tables; its numbers cite those sources"
                data-testid="doc-row-derived"
              >
                derived
              </span>
            )}
            {row.source_classification?.source_doctype && (
              <span className={styles.doctypeBadge}>{row.source_classification.source_doctype}</span>
            )}
            {!row.source_classification?.source_doctype && row.doc_type && (
              <span className={styles.doctypeBadge}>{row.doc_type}</span>
            )}
            {showBlocking && (
              <span className={styles.blockingBadge} aria-label="blocking">
                blocking
              </span>
            )}
            {row.source_classification && <EvidenceSignals row={row} />}
          </div>
          {typeof row.source_classification?.source_doctype_confidence === "number" && (
            <div className={styles.confidenceBarWrap}>
              <ConfidenceBar confidence={row.source_classification.source_doctype_confidence} size="sm" />
            </div>
          )}
          {row.label_params && Object.entries(row.label_params).some(([, v]) => v !== null) && (
            <div className={styles.paramRow} data-testid="doc-row-params">
              {Object.entries(row.label_params)
                .filter(([, v]) => v !== null)
                .map(([key, value]) => (
                  <span key={key} className={styles.paramChip}>
                    <span className={styles.paramChipKey}>{key}:</span>
                    <span className={styles.paramChipValue}>{value}</span>
                  </span>
                ))}
            </div>
          )}
        </div>
        {canDownload && (
          <button
            type="button"
            className={styles.downloadButton}
            onClick={handleDownload}
            aria-label={`Download document ${row.filename}`}
            title="Download this document"
          >
            <ArrowDownload24Regular aria-hidden="true" className={"w-5 h-5"} />
          </button>
        )}
        {onDeleteDoc && (
          <button
            type="button"
            className={styles.deleteButton}
            onClick={handleDelete}
            aria-label={`Delete document ${row.filename}`}
            title="Delete this document"
          >
            <Delete24Regular aria-hidden="true" className={"w-5 h-5"} />
          </button>
        )}
      </div>
    </button>
  );
};

export default DocRow;
